/**
 * Centralised axios client for the seller portal (M19).
 *
 * One axios instance, used everywhere. Replaces the ad-hoc
 * `axios.get/post(...)` calls that were inline in App.js.
 *
 * What this gives us:
 *
 *   1. **Token injection.** A request interceptor reads the bearer
 *      token from `localStorage` on every call.
 *
 *   2. **401 → logout.** A response interceptor catches 401, clears
 *      the token, and dispatches a window event so AuthContext can
 *      react without a circular import.
 *
 *   3. **X-Request-ID propagation.** Every outbound request gets a
 *      fresh UUID for server log correlation.
 *
 *   4. **Single base URL.** Read once from `REACT_APP_BACKEND_URL`.
 *
 * Usage:
 *
 *   import api from '@/services/api';
 *
 *   const quotations = await api.get('/quotations', { params: { reference_number } });
 *   const result = await api.post('/quotations/select', payload);
 *
 * Paths are relative to `/api` — the base URL already includes that prefix.
 */

import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) {
  // eslint-disable-next-line no-console
  console.error(
    "REACT_APP_BACKEND_URL is not set. Check apps/seller-portal/frontend/.env",
  );
}

const API_BASE = `${BACKEND_URL || ""}/api`;

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
});

// ---------------------------------------------------------------------------
// Request interceptor — token + correlation id
// ---------------------------------------------------------------------------
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    config.headers["X-Request-ID"] = crypto.randomUUID();
  }
  return config;
});

// ---------------------------------------------------------------------------
// Response interceptor — 401 → logout
// ---------------------------------------------------------------------------
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      const wasAuthenticated = !!localStorage.getItem("token");
      if (wasAuthenticated) {
        localStorage.removeItem("token");
        localStorage.removeItem("referenceNumber");
        delete api.defaults.headers.common.Authorization;
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("bc:auth:logout"));
        }
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Extract a human-readable error message from an axios error.
 */
export function errorMessage(error) {
  if (!error) return "Unknown error";
  const data = error?.response?.data;
  if (data && typeof data === "object") {
    if (data.error && typeof data.error === "object" && data.error.message) {
      return String(data.error.message);
    }
    if (data.detail) return String(data.detail);
  }
  if (error.message) return String(error.message);
  return "Unknown error";
}

export default api;
